// WeekAheadTV.js — versão TV/BROADCAST do RR Semana à Frente (RRTV)
// ============================================================
// Deriva do WeekAhead.jsx do site (mesma paleta/linguagem: HeroCard, ImpactBadge,
// DataStrip, consenso, angle, person+logo inline), mas:
//   • TV-sized 1920×1080, UM evento por vez (pro cue casar com a narração)
//   • seleção por ?event_id= (ou ?date=&idx=) sobre /api/week-ahead/latest
//   • DETERMINÍSTICO: seta data-tv-ready="1" só quando o card + imagem carregam
//     (o Playwright espera esse sinal → nunca fotografa dado 0/vazio)
//
// Rota (app.js):  <Route path="/tv/week-ahead" element={<WeekAheadTV/>} />
// PARAMS:
//   ?event_id=2026-08-11-macro-usd-cpi-m-m         (seleciona o card; recomendado)
//   ?date=2026-08-11 &idx=0                          (alternativa: dia + índice)
//   ?src=/api/week-ahead/latest  ?imgBase=/week_ahead_images  (overrides opcionais)
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const C = {
  purple: '#6660fe', purpleDim: 'rgba(102,96,254,0.14)', purpleEdge: 'rgba(102,96,254,0.34)',
  bg: '#0e0d1f', text: '#f4f4f6', muted: '#8b8a99', dim: '#5f5e6e',
  gold: '#c9a227', green: '#22c55e', red: '#DC143C',
};
const F_HEAD = "Roble, Georgia, serif";
const F_BODY = "'Plus Jakarta Sans', system-ui, sans-serif";
const F_MONO = "'IBM Plex Mono', monospace";
const IMPACT = {
  High: { label: 'ALTO IMPACTO', color: C.red, bg: 'rgba(220,20,60,.15)' },
  Medium: { label: 'MÉDIO IMPACTO', color: C.gold, bg: 'rgba(201,162,39,.15)' },
  Low: { label: 'BAIXO IMPACTO', color: C.muted, bg: 'rgba(139,138,153,.12)' },
  Holiday: { label: 'FERIADO', color: C.dim, bg: 'rgba(95,94,110,.12)' },
};
const ANGLE = {
  otimista: { label: 'otimista', color: C.green }, cauteloso: { label: 'cauteloso', color: C.red },
  neutro: { label: 'neutro', color: C.muted }, disputado: { label: 'disputado', color: C.gold },
  sem_consenso: { label: 'sem consenso', color: C.dim },
};
const SLOT_PT = { 'Pre Market': 'PRÉ', 'During Market': 'PREGÃO', 'Post Market': 'PÓS' };
const tickerBase = t => (t || '').split('.')[0].toUpperCase();
const shortName = c => (c.kind === 'earnings' && c.company) ? c.company : (c.title || '').split('#')[0].trim();

export default function WeekAheadTV() {
  const [p] = useSearchParams();
  const src = p.get('src') || '/api/week-ahead/latest';
  const imgBase = p.get('imgBase') || '/week_ahead_images';
  const logoBase = p.get('logoBase') || '/api/logos';
  const eventId = p.get('event_id') || '';
  const date = p.get('date') || '';
  const idx = parseInt(p.get('idx') || '0', 10);

  const [card, setCard] = useState(null);
  const [dayLabel, setDayLabel] = useState('');
  const [imgFailed, setImgFailed] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(src);
        if (!r.ok || !alive) return;
        const j = await r.json();
        let found = null, dl = '';
        for (const d of (j.days || [])) {
          const cards = d.cards || [];
          if (eventId) {
            const c = cards.find(x => x.event_id === eventId);
            if (c) { found = c; dl = `${d.weekday_pt || ''} ${(d.date || '').split('-').reverse().slice(0, 2).join('/')}`; break; }
          } else if (date && d.date === date) {
            found = cards[idx] || cards[0]; dl = `${d.weekday_pt || ''} ${(d.date || '').split('-').reverse().slice(0, 2).join('/')}`; break;
          }
        }
        if (!found && !eventId && !date) { // sem seletor: primeiro card do 1º dia
          const d0 = (j.days || [])[0]; if (d0) { found = (d0.cards || [])[0]; dl = d0.weekday_pt || ''; }
        }
        if (alive && found) { setCard(found); setDayLabel(dl); }
      } catch { /* noop */ }
    })();
    return () => { alive = false; };
  }, [src, eventId, date, idx]);

  const imgUrl = card ? `${imgBase}/${card.event_id}.webp` : null;
  const base = card ? tickerBase(card.ticker) : '';
  const logoUrl = base ? `${logoBase}/${base}.svg` : null;
  const showImg = Boolean(imgUrl) && !imgFailed;
  const showLogo = Boolean(logoUrl) && !logoFailed && card && card.kind === 'earnings';

  // READY determinístico: só quando o card resolveu + a imagem de fundo pintou
  useEffect(() => {
    if (!card) return;
    const mark = () => { document.documentElement.setAttribute('data-tv-ready', '1'); window.__TV_READY__ = true; };
    if (!imgUrl) { mark(); return; }
    const im = new Image(); im.onload = mark; im.onerror = mark; im.src = imgUrl;
  }, [card, imgUrl]);

  const imp = card ? (IMPACT[card.impact] || IMPACT.Low) : IMPACT.Low;
  const e = (card && card.enrichment) || {};
  const v = (card && card.visual) || {};
  const d = (card && card.data) || {};
  const angle = ANGLE[e.angle];
  const body = e.consensus || e.historical;

  const dataItems = [];
  if (d.eps_estimate != null) dataItems.push(['EPS EST.', Number(d.eps_estimate).toFixed(2)]);
  if (d.forecast) dataItems.push(['PREVISÃO', d.forecast]);
  if (d.previous) dataItems.push(['ANTERIOR', d.previous]);

  return (
    <div style={{ position: 'relative', width: 1920, height: 1080, overflow: 'hidden',
      background: `linear-gradient(135deg, ${C.bg} 0%, #14132b 55%, #0a0a14 100%)`,
      fontFamily: F_BODY, color: C.text }}>

      {/* imagem temática — metade direita, sangra pra esquerda */}
      {showImg && (
        <img src={imgUrl} alt="" onError={() => setImgFailed(true)}
          style={{ position: 'absolute', right: 0, top: 0, width: '58%', height: '100%', objectFit: 'cover' }} />
      )}
      {/* blend: escurece a junção e a base */}
      <div style={{ position: 'absolute', inset: 0, background:
        `linear-gradient(90deg, ${C.bg} 0%, ${C.bg} 40%, rgba(14,13,31,0.72) 52%, rgba(14,13,31,0.12) 78%, rgba(10,10,20,0.55) 100%),`
        + ` linear-gradient(180deg, rgba(7,7,10,0.30) 0%, transparent 30%, rgba(7,7,10,0.55) 100%)` }} />

      {/* topo: kicker + dia/hora/slot */}
      <div style={{ position: 'absolute', top: 56, left: 72, right: 72, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontFamily: F_MONO, fontSize: 26, fontWeight: 800, color: C.purple, background: C.purpleDim,
          border: `1px solid ${C.purpleEdge}`, padding: '8px 20px', borderRadius: 6, letterSpacing: '0.18em' }}>RR SEMANA À FRENTE</span>
        <span style={{ fontFamily: F_MONO, fontSize: 30, fontWeight: 700, color: C.text, letterSpacing: '0.05em',
          textShadow: '0 2px 10px rgba(0,0,0,0.9)' }}>
          {dayLabel}{card && card.time_local ? `  ·  ${card.time_local}` : ''}
          {card && SLOT_PT[card.slot] ? `  ·  ${SLOT_PT[card.slot]}` : ''}
        </span>
      </div>

      {/* bloco de conteúdo — esquerda */}
      <div style={{ position: 'absolute', left: 72, top: 210, width: 980 }}>
        {/* impacto + ticker + flag */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 22 }}>
          <span style={{ fontFamily: F_MONO, fontSize: 22, fontWeight: 800, letterSpacing: '0.12em',
            color: imp.color, background: imp.bg, border: `1px solid ${imp.color}55`, padding: '7px 16px', borderRadius: 6 }}>
            ● {imp.label}</span>
          {card && card.ticker ? <span style={{ fontFamily: F_MONO, fontSize: 26, fontWeight: 600, color: C.purple, letterSpacing: '0.04em' }}>{tickerBase(card.ticker)}</span> : null}
          {card && card.flag ? <span style={{ fontSize: 40 }}>{card.flag}</span> : null}
        </div>

        {/* título (+ logo inline se earnings c/ foto) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 22 }}>
          {showImg && showLogo ? <img src={logoUrl} alt="" onError={() => setLogoFailed(true)}
            style={{ height: 84, maxWidth: 160, objectFit: 'contain', flexShrink: 0 }} /> : null}
          <h1 style={{ fontFamily: F_HEAD, fontSize: 92, fontWeight: 800, margin: 0, lineHeight: 1.02,
            letterSpacing: '-0.02em', color: C.text, textShadow: '0 3px 22px rgba(0,0,0,0.85)' }}>
            {card ? shortName(card) : 'Semana à Frente'}</h1>
        </div>

        {/* pessoa (CEO/exec) */}
        {v.person ? <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: v.confidence === 'high' ? C.purple : C.dim }} />
          <span style={{ fontFamily: F_BODY, fontSize: 30, fontWeight: 600, color: C.muted }}>{v.person}</span>
        </div> : null}

        {/* dados grandes */}
        {dataItems.length ? <div style={{ display: 'flex', gap: 70, marginTop: 40 }}>
          {dataItems.map(([k, val]) => (
            <div key={k} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontFamily: F_MONO, fontSize: 22, letterSpacing: '0.12em', color: C.dim }}>{k}</span>
              <span style={{ fontFamily: F_HEAD, fontSize: 62, fontWeight: 800, color: k === 'PREVISÃO' || k === 'EPS EST.' ? C.purple : C.text, lineHeight: 1 }}>{val}</span>
            </div>
          ))}
        </div> : null}

        {/* consenso */}
        {body ? <p style={{ fontFamily: F_BODY, fontSize: 30, lineHeight: 1.42, marginTop: 40, maxWidth: 940,
          color: 'rgba(244,244,246,0.92)', textShadow: '0 2px 10px rgba(0,0,0,0.9)',
          borderLeft: `4px solid ${C.purple}`, paddingLeft: 22,
          display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{body}</p> : null}

        {/* angle */}
        {angle ? <div style={{ marginTop: 22, fontFamily: F_MONO, fontSize: 24, color: angle.color, letterSpacing: '0.05em' }}>▸ {angle.label}</div> : null}
      </div>
    </div>
  );
}
